
  const containers = document.querySelectorAll('.menu-container');
  containers.forEach((container) => {
    const tabListContainer = container.querySelector('.menu-tablist-container');
    const tabList = tabListContainer.querySelector('.menu-tablist-container > div');
    const tabButtons = Array.from(tabList.querySelectorAll('button'));
    const panelsContainer = container.querySelector('.menu-container-inner');
    const panels = Array.from(panelsContainer.children);

    if (tabButtons.length !== panels.length) return;

    tabList.setAttribute('role', 'tablist');

    let currentIndex = 0;

    // A tab is disabled when the currently selected venue is in the tab's
    // "hide for these venues" list. Fail-open: no list or no known venue keeps
    // the tab visible.
    function isTabDisabled(index) {
      const panel = panels[index];
      if (!panel || !panel.dataset.hideVenues) return false;

      const venueId = (typeof get_venue === 'function') ? get_venue() : null;
      if (!venueId) return false;

      const excluded = panel.dataset.hideVenues.split(',').filter(Boolean);
      return excluded.includes(String(venueId));
    }

    function firstEnabledIndex() {
      const index = tabButtons.findIndex((_, i) => !isTabDisabled(i));
      return index === -1 ? 0 : index;
    }

    function lastEnabledIndex() {
      for (let i = tabButtons.length - 1; i >= 0; i--) {
        if (!isTabDisabled(i)) return i;
      }
      return 0;
    }

    // Next enabled tab index in the given direction, skipping disabled tabs.
    function stepEnabled(from, direction) {
      const total = tabButtons.length;
      let index = from;
      for (let step = 0; step < total; step++) {
        index = (index + direction + total) % total;
        if (!isTabDisabled(index)) return index;
      }
      return from;
    }

    function applyTabVisibility() {
      tabButtons.forEach((btn, i) => {
        const disabled = isTabDisabled(i);
        btn.style.display = disabled ? 'none' : '';
        if (disabled) {
          btn.setAttribute('aria-hidden', 'true');
          btn.setAttribute('tabindex', '-1');
        } else {
          btn.removeAttribute('aria-hidden');
        }
      });
    }

    tabButtons.forEach((btn, i) => {
      const panel = panels[i];
      const tabId = btn.id || `${container.id || 'tabs'}-tab-${i}`;
      const panelId = panel.id || `${tabId}-panel`;

      btn.id = tabId;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-controls', panelId);

      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
    });

    function updateSelection(index, focus = true) {
      if (isTabDisabled(index)) index = firstEnabledIndex();
      currentIndex = index;

      tabButtons.forEach((btn, i) => {
        btn.setAttribute('aria-selected', i === index ? 'true' : 'false');
        btn.setAttribute('tabindex', i === index ? '0' : '-1');
        btn.classList.toggle('is-open', i === index);
      });
      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      if(focus) tabButtons[index].focus();

      const btn = tabButtons[index];
      if (btn && tabList && typeof tabList.scrollTo === 'function') {
        const btnCenter = btn.offsetLeft + btn.offsetWidth / 2;
        const listCenter = tabListContainer.offsetWidth / 2;
        tabListContainer.scrollTo({
          left: btnCenter - listCenter,
          behavior: 'smooth'
        });
      }

      const panel = panels[index]?.closest('.menu-container');
      if (panel && focus) {
        const rect = panel.getBoundingClientRect();
        const root = document.documentElement;
        const adminBarHeight = parseInt(getComputedStyle(root).getPropertyValue('--wp-admin--admin-bar--height') || '0', 10);
        const quickNavHeight = parseInt(getComputedStyle(root).getPropertyValue('--current-quick-nav-main-height') || '0', 10);
        const mainHeaderHeight = parseInt(getComputedStyle(root).getPropertyValue('--current-header-main-height') || '0', 10);
        const offset = adminBarHeight + quickNavHeight + mainHeaderHeight;
        const isVisible = rect.top >= offset;

        if (!isVisible) {
          const scrollY = window.scrollY + rect.top - offset;
          window.scrollTo({ top: scrollY, behavior: 'smooth' });
        }
      }

      const event = new CustomEvent('tabChanged', { detail: { button: btn, panel: panel } });
      document.body.dispatchEvent(event);
    }

    function updateScrollClasses() {
      const canScrollX = tabListContainer.scrollWidth > tabListContainer.clientWidth;
      const canScrollY = tabListContainer.scrollHeight > tabListContainer.clientHeight;

      let notAtStart = false;
      let notAtEnd = false;

      if (canScrollX) {
        notAtStart ||= tabListContainer.scrollLeft > 0;
        notAtEnd   ||= tabListContainer.scrollLeft + tabListContainer.clientWidth < tabListContainer.scrollWidth;
      }

      if (canScrollY) {
        notAtStart ||= tabListContainer.scrollTop > 0;
        notAtEnd   ||= tabListContainer.scrollTop + tabListContainer.clientHeight < tabListContainer.scrollHeight;
      }

      tabListContainer.parentElement.classList.toggle('not-at-start', notAtStart);
      tabListContainer.parentElement.classList.toggle('not-at-end', notAtEnd);
    }

    function updateHeight() {
      const height = tabListContainer.offsetHeight;
      container.setAttribute('style', `--menu-tab-height: ${height}px`);
    }

    updateScrollClasses();
    updateHeight();
    tabListContainer.addEventListener('scroll', updateScrollClasses);
    window.addEventListener('resize', updateScrollClasses);
    window.addEventListener('resize', () => {
      updateHeight();
      setTimeout(() => {
        updateHeight();
      }, 300);
    });

    tabButtons.forEach((btn, i) => {
      btn.addEventListener('click', () => updateSelection(i));

      btn.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowRight':
            e.preventDefault();
            updateSelection(stepEnabled(i, 1));
            break;
          case 'ArrowLeft':
            e.preventDefault();
            updateSelection(stepEnabled(i, -1));
            break;
          case 'Home':
            e.preventDefault();
            updateSelection(firstEnabledIndex());
            break;
          case 'End':
            e.preventDefault();
            updateSelection(lastEnabledIndex());
            break;
        }
      });
    });

    function selectTabByHash() {
      const hash = window.location.hash.replace('#', '');
      if (!hash) {
        updateSelection(firstEnabledIndex(), false);
        return;
      }

      let index = tabButtons.findIndex(btn => btn.getAttribute('aria-controls') === hash);
      let targetElement = null;

      if (index === -1) {
        targetElement = document.getElementById(hash);
        if (targetElement) {
          index = panels.findIndex(panel => panel.contains(targetElement));
        }
      }

      if (index !== -1) {
        updateSelection(index, false);

        if (targetElement) {
          requestAnimationFrame(() => {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      } else {
        updateSelection(firstEnabledIndex(), false);
      }
    }

    applyTabVisibility();
    selectTabByHash();
    window.addEventListener('popstate', () => selectTabByHash());
    window.addEventListener('hashchange', () => selectTabByHash());

    // Re-evaluate tab visibility when the venue changes (auto-detect on first
    // visit, ?v= param, or the on-page venue selector).
    document.body.addEventListener('venueChanged', () => {
      applyTabVisibility();
      if (isTabDisabled(currentIndex)) {
        updateSelection(firstEnabledIndex(), false);
      }
    });
  });
